// Style Plugins Registry
// Export helpers first so they can be used by plugins
export { createColorProperty, createSimpleProperty } from './helpers.js';

import background from './background.jsx';
import border from './border.jsx';
import outline from './outline.jsx';
import spacing from './spacing.jsx';
import size from './size.jsx';
import font from './font.jsx';
import text from './text.jsx';
import layout from './layout.jsx';
import flexbox from './flexbox.jsx';
import effects from './effects.jsx';
import filters from './filters.jsx';
import tables from './tables.jsx';
import transitions from './transitions.jsx';
import transforms from './transforms.jsx';
import interactivity from './interactivity.jsx';
import svg from './svg.jsx';
import list from './list.jsx';
import accessibility from './accessibility.jsx';
import misc from './misc.jsx';

// Export all plugins in order
export const stylePlugins = [
    layout,
    flexbox,
    spacing,
    size,
    font,
    text,
    list,
    background,
    border,
    outline,
    effects,
    filters,
    tables,
    transitions,
    transforms,
    interactivity,
    svg,
    accessibility,
    misc
];

/**
 * Get plugin that contains a specific property
 */
export const getPluginForProperty = (propertyKey) => {
    return stylePlugins.find(plugin => {
        if (typeof plugin.properties === 'object' && !Array.isArray(plugin.properties)) {
            return propertyKey in plugin.properties;
        }
        return plugin.properties.includes(propertyKey);
    });
};

/**
 * Get all properties from all plugins
 */
export const getAllPluginProperties = () => {
    return stylePlugins.flatMap(plugin => {
        if (typeof plugin.properties === 'object' && !Array.isArray(plugin.properties)) {
            return Object.keys(plugin.properties);
        }
        return plugin.properties;
    });
};

/**
 * Get property definition from its plugin
 */
export const getPropertyDefinition = (propertyKey) => {
    const plugin = getPluginForProperty(propertyKey);
    if (!plugin) return null;

    if (typeof plugin.properties === 'object' && !Array.isArray(plugin.properties)) {
        return plugin.properties[propertyKey];
    }

    // For old-style plugins, return null (they'll need migration)
    return null;
};

/**
 * Get display label for a property within its plugin context
 */
export const getPropertyLabel = (propertyKey, defaultLabel) => {
    const def = getPropertyDefinition(propertyKey);
    if (def && def.label) {
        return def.label;
    }

    // Fallback to old propertyLabels for plugins not yet migrated
    const plugin = getPluginForProperty(propertyKey);
    if (plugin && plugin.propertyLabels && plugin.propertyLabels[propertyKey]) {
        return plugin.propertyLabels[propertyKey];
    }

    return defaultLabel;
};

/**
 * Convert property values to Tailwind classes
 * @param {Object} propertyValues - Object with property keys and their values
 * @returns {string} Space-separated Tailwind classes
 */
export const propertiesToClasses = (propertyValues) => {
    const classes = [];

    Object.entries(propertyValues).forEach(([key, value]) => {
        if (!value) return;

        const def = getPropertyDefinition(key);
        if (def && def.toClass) {
            const className = def.toClass(value);
            if (className) {
                classes.push(className);
            }
        }
    });

    return classes.join(' ');
};

/**
 * Parse Tailwind classes into property values
 * @param {string} classString - Space-separated Tailwind classes
 * @returns {Object} Property keys mapped to values
 */
export const classesToProperties = (classString) => {
    if (!classString) return {};

    const classes = classString.split(' ').map(c => c.trim()).filter(Boolean);
    const properties = {};

    // Track which classes have been matched to avoid duplicates
    const matchedClasses = new Set();

    // Iterate through all plugins and their properties
    stylePlugins.forEach(plugin => {
        if (typeof plugin.properties === 'object' && !Array.isArray(plugin.properties)) {
            Object.entries(plugin.properties).forEach(([propertyKey, def]) => {
                if (!def.fromClass) return;

                // Try to match ALL classes to this property (CSS cascade: last wins)
                for (const className of classes) {
                    if (matchedClasses.has(className)) continue;

                    const value = def.fromClass(className);
                    if (value !== null && value !== undefined) {
                        properties[propertyKey] = value; // Overwrite with latest value
                        matchedClasses.add(className);
                        // Don't break - continue checking for more matches (duplicates)
                    }
                }
            });
        }
    });

    return properties;
};

/**
 * Get inline styles for properties that can't be represented as Tailwind classes
 * (e.g., hex colors, custom values)
 * @param {Object} propertyValues - Object with property keys and their values
 * @param {Function} resolveColor - Function to resolve theme color keys to CSS colors
 * @returns {Object} CSS properties object
 */
export const propertiesToInlineStyles = (propertyValues, resolveColor) => {
    const styles = {};

    Object.entries(propertyValues).forEach(([key, value]) => {
        if (!value) return;

        // Handle colors that are hex/rgb/hsl
        if (key.includes('Color') && (value.startsWith('#') || value.startsWith('rgb') || value.startsWith('hsl'))) {
            const resolved = resolveColor ? resolveColor(value) : value;

            if (key === 'backgroundColor') {
                styles.backgroundColor = resolved;
            } else if (key === 'textColor') {
                styles.color = resolved;
            } else if (key === 'borderColor') {
                styles.borderColor = resolved;
            } else if (key === 'outlineColor') {
                styles.outlineColor = resolved;
            } else if (key === 'boxShadowColor') {
                // Shadow colors need special handling
                styles.boxShadow = `0 0 0 1px ${resolved}`;
            }
        }
    });

    return styles;
};

/**
 * Clean a class string by removing duplicate property values and migrating old color syntax
 * Parses the string into properties, then regenerates to ensure only one value per property
 * Also converts old format text-#HEXCODE to new format text-[#HEXCODE]
 * @param {string} classString - Space-separated Tailwind classes (may contain duplicates)
 * @returns {string} Cleaned class string with duplicates removed
 */
export const cleanDuplicateClasses = (classString) => {
    if (!classString) return '';

    // Fix detached prefixes (e.g. "hover: bg-red") -> "hover:bg-red"
    // Also handles "hover: variant-data-[...]"
    let fixedString = classString.replace(/([a-z0-9-_]+:)\s+(?=[a-z0-9-_\[\]#])/gi, '$1');

    // First, migrate old color syntax (text-#HEXCODE) to new syntax (text-[#HEXCODE])
    let migratedString = fixedString;
    const oldColorRegex = /(text|bg|border|decoration|shadow|ring|outline|divide|from|via|to|accent)-(#[0-9a-fA-F]{3,8}|rgb\([^)]+\)|hsl\([^)]+\))/g;
    migratedString = migratedString.replace(oldColorRegex, (match, prefix, color) => {
        // Only add brackets if not already present
        if (!match.includes('[')) {
            return `${prefix}-[${color}]`;
        }
        return match;
    });

    // Parse classes into property object (automatically deduplicates)
    const properties = classesToProperties(migratedString);

    // Regenerate class string from properties (clean output)
    const cleanedClasses = propertiesToClasses(properties);

    // However, some classes might not be recognized by the plugin system
    // Keep those unmatched classes as-is
    const originalClasses = migratedString.split(' ').map(c => c.trim()).filter(Boolean);
    const recognizedClasses = cleanedClasses.split(' ').filter(Boolean);

    // Find classes that weren't parsed by the plugin system
    const unmatchedClasses = originalClasses.filter(cls => {
        // Check if this class was converted to a property
        let matched = false;
        stylePlugins.forEach(plugin => {
            if (typeof plugin.properties === 'object' && !Array.isArray(plugin.properties)) {
                Object.values(plugin.properties).forEach(def => {
                    if (def.fromClass && def.fromClass(cls) !== null) {
                        matched = true;
                    }
                });
            }
        });
        return !matched;
    });

    // Combine cleaned classes with unmatched classes
    return [...recognizedClasses, ...unmatchedClasses].join(' ');
};
